<template>
	<section
		:class="
			bemm('', [
				active ? 'is-open' : '',
				color ? 'has-color' : '',
				disabled ? 'disabled' : '',
			])
		"
		:style="color ? `--collapse-color: var(--color-${color})` : ``"
	>
		<div :class="bemm('header-row')">
			<button
				:class="bemm('header-main')"
				:aria-expanded="active"
				:aria-controls="`content-${itemId}`"
				@click="activate()"
			>
				<span :class="bemm('title')">
					<slot name="title" :active="active">
						<Icon v-if="icon" :name="icon" :class="bemm('title-icon')" />
						{{ title }}
					</slot>
				</span>
			</button>
			<div v-if="$slots.middle" :class="bemm('middle')">
				<slot name="middle" :active="active"></slot>
			</div>
			<div v-if="$slots.actions" :class="bemm('actions')">
				<slot name="actions" :active="active"></slot>
			</div>
			<button
				:class="bemm('header-toggle')"
				:aria-expanded="active"
				:aria-controls="`content-${itemId}`"
				@click="activate()"
			>
				<span :class="bemm('chevron')">
					<Icon :name="Icons.CHEVRON_DOWN" />
				</span>
			</button>
		</div>
		<div
			v-if="keepAlive || active"
			v-show="active"
			:id="`content-${itemId}`"
			:class="bemm('content')"
			role="region"
			:aria-label="title"
			:aria-hidden="!active"
		>
			<div :class="bemm('body')">
				<slot :active="active"></slot>
			</div>
		</div>
	</section>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { useBemm } from 'bemm';
import { Color } from '@arritech/shared/types';
import { Icons } from '../Icon';
import { Icon, type IconType } from '../Icon';
import type { CollapseItemInstance, CollapseKey } from './Collapse.model';
import { useCollapseContext } from './useCollapse';

const emit = defineEmits(['update:modelValue', 'input', 'change']);

const props = defineProps({
	modelValue: {
		type: Boolean as PropType<boolean | undefined>,
		default: undefined,
	},
	value: {
		type: Boolean as PropType<boolean | undefined>,
		default: undefined,
	},
	title: {
		type: String as PropType<string>,
		default: '',
	},
	name: {
		type: [String, Number] as PropType<CollapseKey | undefined>,
		default: undefined,
	},
	id: {
		type: String as PropType<string | undefined>,
		default: undefined,
	},
	color: {
		type: String as PropType<Color | undefined>,
		default: undefined,
	},
	icon: {
		type: String as PropType<IconType | undefined>,
		default: undefined,
	},
	keepAlive: {
		type: Boolean as PropType<boolean>,
		default: false,
	},
	disabled: {
		type: Boolean as PropType<boolean>,
		default: false,
	},
});

const bemm = useBemm('ar-collapse-item', {
	return: 'string',
	includeBaseClass: true,
});

const collapseContext = useCollapseContext();
const active = ref(false);

const itemId = computed(() => {
	return String(props.id ?? props.name ?? props.title ?? 'collapse-item');
});

const itemKey = computed<CollapseKey>(() => {
	return (props.name ??
		props.id ??
		props.title ??
		'collapse-item') as CollapseKey;
});

const setActive = (next: boolean) => {
	active.value = next;
};

const itemInstance: CollapseItemInstance = {
	itemKey: itemKey.value,
	setActive,
};

const activate = () => {
	if (props.disabled) return;

	if (!collapseContext?.isCollapseParent) {
		setActive(!active.value);
		return;
	}

	collapseContext.toggleItem(itemInstance);
};

onMounted(() => {
	if (!collapseContext?.isCollapseParent) {
		return;
	}

	collapseContext.addItem(itemInstance);
});

onUnmounted(() => {
	if (!collapseContext?.isCollapseParent) {
		return;
	}

	collapseContext.removeItem(itemInstance);
});

watch(
	itemKey,
	(nextKey) => {
		itemInstance.itemKey = nextKey;
	},
	{
		immediate: true,
	}
);

watch(
	() => props.modelValue,
	(newValue) => {
		if (typeof newValue === 'boolean') {
			active.value = newValue;
		}
	},
	{
		immediate: true,
	}
);

watch(
	() => props.value,
	(newValue) => {
		if (typeof newValue === 'boolean') {
			active.value = newValue;
		}
	},
	{
		immediate: true,
	}
);

watch(active, (newValue) => {
	emit('update:modelValue', newValue);
	emit('change', newValue);
	emit('input', newValue);
});
</script>

<style lang="scss">
.ar-collapse-item {
	$b: &;
	border-radius: var(--border-radius);
	border: 1px solid
		var(
			--collapse-item-border-color,
			color-mix(in srgb, var(--color-foreground), transparent 88%)
		);
	background: var(--collapse-item-background-color, var(--color-background));

	&--has-color {
		--collapse-item-border-color: color-mix(
			in srgb,
			var(--collapse-color, var(--color-foreground)),
			transparent 50%
		);
		--collapse-item-background-color: color-mix(
			in srgb,
			var(--collapse-color, var(--color-background)),
			transparent 95%
		);
		--collapse-item-background-color--hover: color-mix(
			in srgb,
			var(--collapse-color, var(--color-background)),
			transparent 80%
		);
	}

	&--disabled {
		opacity: 0.6;

		#{$b}__header-main,
		#{$b}__header-toggle {
			cursor: default;
			pointer-events: none;
		}

		#{$b}__chevron {
			visibility: hidden;
		}
	}

	&__header-main,
	&__header-toggle {
		flex: 1 1 auto;
		display: flex;
		align-items: center;
		gap: var(--space);
		padding: var(--space) var(--space);
		background: transparent;
		border: 0;
		text-align: left;
		font: inherit;
		color: inherit;
		cursor: pointer;
		transition:
			background-color 0.2s,
			color 0.2s;
		border-radius: inherit;
		appearance: none;

		&:focus-visible {
			outline: none;
			box-shadow: 0 0 0 4px
				color-mix(in srgb, var(--color-primary), var(--color-background) 80%);
		}
	}

	&__header-main {
		min-width: 0;
		justify-content: flex-start;
	}

	&__header-toggle {
		flex: 0 0 auto;
		justify-content: center;
		padding-left: var(--space-s);
		padding-right: var(--space-s);
	}

	&__header-row {
		display: flex;
		align-items: stretch;
		gap: var(--space-s);
		padding: 0;
		background: var(--collapse-item-background-color, var(--color-background));
		border-radius: inherit;
	}

	&__title {
		font-weight: var(--font-weight-bold);
		display: flex;
		align-items: center;
		gap: var(--space-xs);
		flex: 1;
		min-width: 0;
	}

	&__middle {
		display: flex;
		align-items: center;
		flex: 0 1 auto;
		min-width: 0;
		padding-top: var(--space-xs);
		padding-bottom: var(--space-xs);
	}

	&__actions {
		display: inline-flex;
		align-items: center;
		padding-right: var(--space);
		padding-top: var(--space-xs);
		padding-bottom: var(--space-xs);
	}
	&__title-icon {
		opacity: 0.5;
	}

	&__chevron {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		transition: transform 0.2s ease;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	&__content {
		max-height: 0;
		opacity: 0;
		transition:
			max-height 0.25s ease,
			opacity 0.2s ease;
		border-top: 1px solid var(--collapse-item-border-color);
		pointer-events: none;
		display: none;
	}

	&__body {
		padding: 0 var(--space);
	}

	&--is-open {
		> #{$b}__content {
			max-height: fit-content;
			opacity: 1;
			pointer-events: all;
			display: block;
		}

		> #{$b}__content > #{$b}__body {
			padding: var(--space);
		}

		> #{$b}__header-toggle #{$b}__chevron {
			transform: rotate(180deg);
		}
	}
}
</style>
